// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { Button, DataTableCell, DataTableRow, LoadingSpinner, Typography } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import HistoryContext, { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import NonTranslationTypography from "@sc/App/Components/NonTranslationTypography/NonTranslationTypography";
import { TRANSLATE_OBJECT } from "@sc/XDL/constants";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    // XNG-Changes
    isReordering: state.pages.orderHistory.isReordering,
    isUpdatingCart: state.context.isUpdatingCart,
    showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
    isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
    showPoNumber: getSettingsCollection(state).orderSettings.showPoNumber,
});

const mapDispatchToProps = {
    reorder,
};

interface CmsProperties {
    showOrderNumber?: boolean;
    showDate?: boolean;
    showOrderTotal?: boolean;
    showStatus?: boolean;
    showShipTo?: boolean;
    showPONumber?: boolean;
    showReorderProducts?: boolean;
}

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
    // XNG-Changes
    cmsProperties?: CmsProperties;
    addTranslateAttribute?: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    clickableCell?: LinkPresentationProps;
    dataCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    customerPOCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    totalCells?: DataTableCellProps;
    actionsCells?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0 20px;
            max-width: 500px;
        `,
    },
    container: { gap: 5 },
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: { width: 6 },
};

const OrderSummaryCard: FC<Props> = ({
    language,
    order,
    orderSettings,
    extendedStyles,
    // XNG-Changes
    isReordering,
    isUpdatingCart,
    reorder,
    showAddToCartConfirmationDialog,
    history,
    isVmiOrderHistoryPage,
    cmsProperties,
    showPoNumber,
    addTranslateAttribute,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));
    // XNG-Changes
    const toaster = useToaster();
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;
    const { orderDate, customerPO, status, orderGrandTotalDisplay } = order;

    const date = getLocalizedDateTime({
        dateTime: orderDate,
        language,
        options: {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        },
    });

    // XNG-Changes
    const shipTo = `${order.stCompanyName} ${order.stAddress1} ${order.stAddress2} ${order.shipToCity} ${order.shipToState}`;

    // XNG-Changes
    const onReorderSuccess = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) => {
        if (!showAddToCartConfirmationDialog) {
            return;
        }
        toaster.addToast({
            body: (
                <HistoryContext.Provider value={{ history }}>
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={linkOrderNumber}
                        isVmiOrder={isVmiOrder}
                        isVmiOrderDetailsPage={isVmiOrderHistoryPage}
                    />
                    &nbsp;{translate("Added to Cart")}
                </HistoryContext.Provider>
            ),
            messageType: "success",
            timeoutLength: 6000,
        });
    };

    // XNG-Changes
    const onReorderError = (message: string) => {
        toaster.addToast({
            body: message,
            messageType: "danger",
            timeoutLength: 6000,
        });
    };

    // XNG-Changes
    const reorderClick = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) =>
        reorder({
            orderNumber,
            onSuccess: () => onReorderSuccess(orderNumber, linkOrderNumber, isVmiOrder),
            onError: message => onReorderError(message),
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.();
                }
            },
        });

    // XNG-Change
    const translateObj = addTranslateAttribute ? TRANSLATE_OBJECT : {};

    // XNG-Changes: add translate attribute for ticket SC-954
    return (
        <DataTableRow>
            {cmsProperties?.showDate && (
                <DataTableCell {...styles.dataCells}>
                    <NonTranslationTypography>{date}</NonTranslationTypography>
                </DataTableCell>
            )}
            {cmsProperties?.showOrderNumber && (
                <DataTableCell
                    className="page-type-link-grid-item"
                    data-test-selector="orderSummaryCard_Date"
                    {...styles.clickableCell}
                >
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={orderNumber}
                        isVmiOrder={!!order.vmiLocationId}
                        isVmiOrderDetailsPage={isVmiOrderHistoryPage}
                    />
                </DataTableCell>
            )}
            {cmsProperties?.showShipTo && (
                <DataTableCell {...styles.dataCells}>
                    {/* XNG-Changes: make only first cell clicakble */}
                    <NonTranslationTypography>{shipTo}</NonTranslationTypography>
                </DataTableCell>
            )}
            {cmsProperties?.showPONumber && showPoNumber && (
                <DataTableCell {...styles.dataCells}>
                    {/* XNG-Changes: make only first cell clicakble */}
                    <NonTranslationTypography>{customerPO || "- -"}</NonTranslationTypography>
                </DataTableCell>
            )}
            {cmsProperties?.showStatus && (
                <DataTableCell {...styles.statusCells} className="status-grid-item">
                    {/* XNG-Changes: make only first cell clicakble */}
                    <NonTranslationTypography>{status}</NonTranslationTypography>
                </DataTableCell>
            )}
            {cmsProperties?.showOrderTotal && (
                <DataTableCell {...styles.dataCells} className="total-grid-item">
                    {/* XNG-Changes: make only first cell clicakble */}
                    <NonTranslationTypography>{orderGrandTotalDisplay}</NonTranslationTypography>
                </DataTableCell>
            )}
            {cmsProperties?.showReorderProducts && (
                <DataTableCell {...styles.actionsCells}>
                    {isReordering[orderNumber] ? (
                        <LoadingSpinner {...styles.reorderButtonSpinner} />
                    ) : (
                        <Button
                            disabled={Object.keys(isReordering).length > 0 || isUpdatingCart}
                            {...styles.reorderButton}
                            onClick={() => reorderClick(orderNumber, orderNumber, !!order.vmiLocationId)}
                            data-test-selector="orderHistoryTable_Reorder"
                            aria-label="Reorder"
                        >
                            {translate("Reorder")}
                        </Button>
                    )}
                </DataTableCell>
            )}
        </DataTableRow>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderSummaryCard));
